import { useCallback, useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { Typography } from "@mui/material";
import { CustomInput } from "../Dynamic/Utils";
import { textClasses } from "../utils/theme";
import { customAPI, debounce } from "../../services/functions";

/**
 * DynamicFilter - Renders refinement filters dynamically from table header attributes.
 *
 * Exposes `getFilters()` via ref — parent calls it to get current dynamic filter values
 * as an array of { path, value } objects ready for the backend.
 *
 * filterConfig shape (same as DynamicForm attributes):
 * {
 *   _id, type, label, placeholder, value, values,
 *   dynamic, searchableDropdown, api, apiMethod,
 *   apiParameters, apiBindingKey, apiBindingValue,
 *   apiDataReturnKey, sequenceNo
 * }
 */
const DynamicFilter = forwardRef(({ attributes = [] }, ref) => {
	// Extract filterConfig from attributes, sorted by sequenceNo
	const filterConfigs = useMemo(() => {
		return attributes
			.filter((attr) => attr.filterConfig && attr.isSearchable)
			.map((attr) => ({
				...attr.filterConfig,
				_parentKey: attr.key || attr.name,
			}))
			.sort((a, b) => (a.sequenceNo ?? 99) - (b.sequenceNo ?? 99));
	}, [attributes]);

	const [filterAttributes, setFilterAttributes] = useState([]);
	const [errors, setErrors] = useState({});
	const [loadingFields, setLoadingFields] = useState({});
	const isInitialised = useRef(false);
	const filterAttrsRef = useRef([]);

	// Keep ref in sync with state so getFilters() always reads latest
	useEffect(() => {
		filterAttrsRef.current = filterAttributes;
	}, [filterAttributes]);

	// Initialize filter attributes only once when configs first arrive
	useEffect(() => {
		if (!filterConfigs.length || isInitialised.current) return;
		isInitialised.current = true;

		const attrs = filterConfigs.map((cfg) => ({
			...cfg,
			value: cfg.value ?? "",
		}));
		setFilterAttributes(attrs);
		filterAttrsRef.current = attrs;
	}, [filterConfigs]);

	// Expose getFilters() and clearFilters() to parent via ref
	useImperativeHandle(ref, () => ({
		getFilters: () => {
			const filters = [];
			filterAttrsRef.current.forEach((attr) => {
				if (attr.value) {
					filters.push({ path: attr._parentKey || attr._id, value: attr.value });
				}
			});
			return filters;
		},
		clearFilters: () => {
			setFilterAttributes((old) => old.map((attr) => ({ ...attr, value: "" })));
		},
	}));

	// --- API fetching for dynamic dropdowns ---

	function fetchSourceDropdownData(field, inputValue = "", cb = null) {
		const { _id, api, apiMethod = "post", apiDataReturnKey = "data", domain } = field;
		if (!api) return;

		let payload = buildAPIPayload(field, inputValue);
		let authInfo = { auth: field.auth, authMethod: field.authMethod, authParams: field.authParams };

		setLoadingFields((o) => ({ ...o, [_id]: true }));
		customAPI(payload, api, apiMethod, domain, authInfo).then((res) => {
			setLoadingFields((o) => ({ ...o, [_id]: false }));
			if (!res.error) {
				let data = res[apiDataReturnKey] || [];
				updateFilterAttr(_id, data, "values");
				if (cb) cb(data);
			} else {
				updateFilterAttr(_id, [], "values");
			}
		});
	}

	function buildAPIPayload(field, searchValue = "") {
		const { apiMethod = "post", apiParameters = [] } = field;
		if (apiMethod.toLowerCase() === "get") {
			return apiParameters
				.map((p) => `${encodeURIComponent(p.label)}=${encodeURIComponent(p.value === "onSearch" ? searchValue : p.value)}`)
				.join("&");
		}
		let payload = {};
		apiParameters.forEach((p) => {
			payload[p.label] = p.value === "onSearch" ? searchValue : p.value;
		});
		return payload;
	}

	function updateFilterAttr(id, data, key = "values") {
		setFilterAttributes((old) =>
			old.map((attr) => (attr._id === id ? { ...attr, [key]: data } : attr))
		);
	}

	const handleDropdownSearch = useCallback(
		debounce((field, inputValue) => {
			fetchSourceDropdownData(field, inputValue);
		}, 500),
		[]
	);

	// --- Value change handlers ---

	const handleDropdownChange = (field, newVal) => {
		updateFilterAttr(field._id, newVal, "value");
	};

	const handleInputChange = (field, value) => {
		updateFilterAttr(field._id, value, "value");
	};

	if (!filterAttributes.length) return null;

	return (
		<>
			{filterAttributes.map((component) => (
				<div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: "0.25rem" }} key={component._id}>
					<CustomInput
						component={component}
						errors={errors}
						fetchSourceDropdownData={fetchSourceDropdownData}
						onDropdownChange={(newVal) => {
							handleDropdownChange(component, newVal);
						}}
						onInputChange={(e) => {
							handleInputChange(component, e.target.value);
						}}
						onQuickAddSave={() => {}}
						onFetchBtnClick={() => {}}
						onTableRowSelect={() => {}}
						loadingFields={loadingFields}
						filterOnly={true}
					/>
					<Typography sx={{ ...textClasses.t12n, color: "#e74c3c", textAlign: "left", mt: "5px", ml: "5px" }}>
						{errors[component._id] || ""}
					</Typography>
				</div>
			))}
		</>
	);
});

DynamicFilter.displayName = "DynamicFilter";

export default DynamicFilter;
